import { createClient, SupabaseClient } from '@supabase/supabase-js';

const env = (import.meta as any).env || {};
const supabaseUrl = env.VITE_SUPABASE_URL || process.env.SUPABASE_URL || '';
const supabaseKey = env.VITE_SUPABASE_ANON_KEY || process.env.SUPABASE_API_KEY || '';

export const supabase: SupabaseClient | null = (supabaseUrl && supabaseKey && !supabaseUrl.includes('placeholder'))
  ? createClient(supabaseUrl, supabaseKey)
  : null;

export interface ContactFormData {
  submissionId?: string;
  fullName: string;
  email: string;
  phone?: string;
  organization?: string;
  serviceRequirement?: string;
  estimatedBudget?: string;
  message: string;
}

export interface CareerApplicationData {
  submissionId?: string;
  fullName: string;
  email: string;
  phone: string;
  location?: string;
  communicationAddress?: string;
  aadhaarNumber?: string;
  panNumber?: string;
  preferredWorkMode?: string;
  appliedRole: string;
  experienceYears?: string;
  skills?: string[] | string;
  availability?: string;
  expectedRate?: string;
  ownSystem?: string;
  internetType?: string;
  powerBackup?: string;
  portfolioUrl?: string;
  linkedinUrl?: string;
  resumeDriveUrl?: string;
  coverLetter?: string;
}

/**
 * Client-side helper to submit contact form directly to Supabase
 */
export async function submitContactToSupabase(data: ContactFormData) {
  if (!supabase) return { success: false, reason: 'Supabase client not initialized' };

  try {
    const payload = {
      submission_id: data.submissionId || `SUB-${Date.now()}`,
      full_name: data.fullName,
      email: data.email,
      phone: data.phone || '',
      organization: data.organization || '',
      service_requirement: data.serviceRequirement || '',
      estimated_budget: data.estimatedBudget || '',
      message: data.message,
      created_at: new Date().toISOString()
    };

    let { data: resData, error } = await supabase.from('contact_submissions').insert([payload]);

    if (error && error.code === '42P01') {
      const retry = await supabase.from('contacts').insert([payload]);
      resData = retry.data;
      error = retry.error;
    }

    if (error) {
      console.error('Supabase contact submission error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: resData };
  } catch (err: any) {
    console.error('Supabase client exception:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Client-side helper to submit job application to Supabase
 */
export async function submitCareerToSupabase(data: CareerApplicationData) {
  if (!supabase) return { success: false, reason: 'Supabase client not initialized' };

  try {
    const payload = {
      submission_id: data.submissionId || `CAREER-${Date.now()}`,
      full_name: data.fullName,
      email: data.email,
      phone: data.phone,
      location: data.location || 'Remote',
      communication_address: data.communicationAddress || '',
      aadhaar_number: data.aadhaarNumber || '',
      pan_number: data.panNumber || '',
      preferred_work_mode: data.preferredWorkMode || 'Remote',
      applied_role: data.appliedRole || 'Freelance Specialist',
      experience_years: data.experienceYears || '',
      skills: Array.isArray(data.skills) ? data.skills.join(', ') : (data.skills || ''),
      availability: data.availability || '',
      expected_rate: data.expectedRate || '',
      own_system: data.ownSystem || '',
      internet_type: data.internetType || '',
      power_backup: data.powerBackup || '',
      portfolio_url: data.portfolioUrl || '',
      linkedin_url: data.linkedinUrl || '',
      resume_drive_url: data.resumeDriveUrl || '',
      cover_letter: data.coverLetter || '',
      created_at: new Date().toISOString()
    };

    let { data: resData, error } = await supabase.from('job_applications').insert([payload]);

    if (error && error.code === '42P01') {
      const retry = await supabase.from('careers').insert([payload]);
      resData = retry.data;
      error = retry.error;
    }

    if (error) {
      console.error('Supabase job application error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: resData };
  } catch (err: any) {
    console.error('Supabase client exception:', err);
    return { success: false, error: err.message };
  }
}
